/* eslint-disable max-lines */
import {
  AimOutlined,
  AlignLeftOutlined,
  AppstoreOutlined,
  CalendarOutlined,
  EuroCircleOutlined,
  FileDoneOutlined,
  InfoCircleOutlined,
  LineChartOutlined,
  OrderedListOutlined,
  SettingOutlined,
  ToolOutlined,
  TruckOutlined,
  UnorderedListOutlined,
  UsergroupAddOutlined
} from '@ant-design/icons'
import { MenuProps, Space } from 'antd'
import { Menu as AntMenu } from 'antd'
import { ReactComponent as ExternalLinkOutlined } from 'app/../../public/assets/images/icons/external_link_icon.svg'
import { useCurrentCustomer } from 'app/lib/hooks/useCurrent'
import { useHelpUrl } from 'app/lib/hooks/useHelpUrl'
import { managerRoles, useRestriction } from 'app/lib/hooks/useRestriction'
import useTranslation from 'app/lib/hooks/useTranslation'
import { Routes } from 'app/routes/Routes'
import { Tractor } from 'lucide-react'
import { MenuInfo } from 'rc-menu/lib/interface'
import * as React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import testIds from 'test_ids.json'

type MenuItemProps = Required<MenuProps>['items'][number] & { children?: MenuItemProps[] }

const getItem = ({ allowFor, ...others }: MenuItemProps & { allowFor: RoleType[] }) => {
  const { restricted } = useRestriction({
    allowFor
  })

  return restricted ? undefined : others
}

const Menu: React.FC<MenuProps> = ({ onClick = () => void 0 }) => {
  const { tComponent } = useTranslation({ componentType: 'navigation', scope: 'sidebar' })

  const helpUrl = useHelpUrl()
  const { has_own_machines_access } = useCurrentCustomer()

  const getOwnMachineMenuItem = (): MenuItemProps => {
    if (!has_own_machines_access) {
      return {
        label: tComponent({ id: 'own_machines' }),
        key: Routes.ProductInstance.collection().index,
        icon: <Tractor />
      }
    }

    return {
      label: tComponent({ id: 'own_machines' }),
      key: `${Routes.ProductInstance.collection().index}-main`,
      icon: <Tractor />,
      children: [
        {
          label: tComponent({ id: 'own_machines_list' }),
          key: Routes.ProductInstance.collection().index,
          icon: <OrderedListOutlined />
        },
        {
          label: tComponent({ id: 'scheduler' }),
          key: Routes.ProductInstance.collection().timeline,
          icon: <CalendarOutlined />
        }
      ]
    }
  }

  const menuItems: MenuItemProps[] = [
    {
      label: tComponent({ id: 'overview' }),
      key: Routes.Overview.collection().index,
      icon: <AppstoreOutlined />
    },
    {
      label: tComponent({ id: 'rentals' }),
      key: Routes.Rental.collection().index,
      icon: <UnorderedListOutlined />
    },
    {
      label: tComponent({ id: 'project' }),
      key: `${Routes.Project.collection().index}-main`,
      icon: <AlignLeftOutlined rotate={90} />,
      children: [
        {
          label: tComponent({ id: 'project.list' }),
          key: Routes.Project.collection().index,
          icon: <OrderedListOutlined />
        },
        {
          label: tComponent({ id: 'scheduler' }),
          key: Routes.Project.collection().timeline,
          icon: <CalendarOutlined />
        },
        {
          label: tComponent({ id: 'project.map' }),
          key: Routes.Project.collection().map,
          icon: <AimOutlined />
        }
      ]
    },
    {
      label: tComponent({ id: 'machines' }),
      key: Routes.Machine.collection().index,
      icon: <TruckOutlined />
    },
    getOwnMachineMenuItem(),
    {
      label: tComponent({ id: 'damageInfo' }),
      key: Routes.DamageInfo.collection().index,
      icon: <ToolOutlined />
    },
    getItem({
      label: tComponent({ id: 'invoices' }),
      key: `${Routes.Invoice.collection().index}-main}`,
      icon: <FileDoneOutlined />,
      allowFor: managerRoles,
      children: [
        {
          label: tComponent({ id: 'invoices' }),
          key: Routes.Invoice.collection().index,
          icon: <FileDoneOutlined />
        },
        {
          label: tComponent({ id: 'monthlyOverview' }),
          key: Routes.Invoice.collection().overview,
          icon: <FileDoneOutlined />
        }
      ]
    }),
    {
      label: tComponent({ id: 'priceList' }),
      key: Routes.PriceList.collection().index,
      icon: <EuroCircleOutlined />
    },
    getItem({
      label: tComponent({ id: 'users' }),
      key: Routes.User.collection().index,
      icon: <UsergroupAddOutlined />,
      allowFor: managerRoles
    }),
    getItem({
      label: tComponent({ id: 'reports' }),
      key: Routes.Report.collection().index,
      icon: <LineChartOutlined />,
      allowFor: managerRoles
    }),
    getItem({
      label: tComponent({ id: 'settings' }),
      key: Routes.Setting.collection().index,
      icon: <SettingOutlined />,
      allowFor: managerRoles
    }),
    {
      label: (
        <Space size={0}>
          {tComponent({ id: 'help_center' })}{' '}
          <span className="d-flex pl-2 menu-icon" role="img">
            <ExternalLinkOutlined />
          </span>
        </Space>
      ),
      key: 'help_center',
      icon: <InfoCircleOutlined />
    }
  ].filter(Boolean)

  const navigate = useNavigate()
  const { pathname } = useLocation()

  const getSelectedKeys = () => {
    const locationArr = pathname.slice(1).split('/')

    const basePath = `/${locationArr[0]}`

    if (locationArr[1]) {
      const combinedPath = `${basePath}/${locationArr[1]}`
      const subMenuKeys = menuItems
        .filter((i) => Boolean(i.children?.length))
        .flatMap((i) => i.children)
        .map((i) => i.key)

      if (subMenuKeys.includes(combinedPath)) {
        return combinedPath
      } else {
        return basePath
      }
    } else {
      return basePath
    }
  }

  const onItemClick = (args: MenuInfo) => {
    if (args.key === 'help_center') {
      window.open(helpUrl)
    } else {
      navigate(args.key)
    }
    onClick(args)
  }

  const activeKey = getSelectedKeys()

  return (
    <AntMenu
      defaultSelectedKeys={['/']}
      selectedKeys={[activeKey]}
      mode="inline"
      onClick={onItemClick}
      data-testid={testIds.ui_elements.layout.menu}
      items={menuItems}
    />
  )
}

export default Menu
